import { supabase } from '../../../lib/supabase';
import type { DateRange } from '../../../components/shared/filters/DateRangePicker';
import type { AiConversationSummary } from './types';
import { fetchFirstResponseMinutesByConversationIds } from './fetchFirstResponseMinutes';

const MAX_ROWS = 2000;

export type ContactEmbed = {
    origins?: { name?: string | null } | { name?: string | null }[] | null;
} | null;

/** Resolve origem de negócio para análise de IA. */
export function resolveOriginLabel(opts: {
    originName?: string | null;
    hasMetaAttribution?: boolean;
}): string {
    const name = opts.originName?.trim();
    if (name) return name;
    if (opts.hasMetaAttribution) return 'Meta Ads';
    return 'Sem origem';
}

function originNameFromEmbed(contact: ContactEmbed): string | null {
    if (!contact?.origins) return null;
    const origins = contact.origins;
    const row = Array.isArray(origins) ? origins[0] : origins;
    return row?.name ?? null;
}

export function conversationOriginLabel(contact: ContactEmbed, metaAttribution: unknown): string {
    return resolveOriginLabel({
        originName: originNameFromEmbed(contact),
        hasMetaAttribution: metaAttribution != null,
    });
}

/** Summaries de atendimentos + origem + tempo até 1ª resposta. */
export async function fetchConversationSummaries(
    organizationId: string,
    dateRange?: DateRange
): Promise<AiConversationSummary[]> {
    let query = supabase
        .from('chat_conversations')
        .select(
            `
            id,
            assigned_to,
            status,
            last_message_time,
            meta_attribution,
            contact:contacts (
                origins ( name )
            )
        `
        )
        .eq('organization_id', organizationId)
        .order('last_message_time', { ascending: false })
        .limit(MAX_ROWS);

    if (dateRange?.start && dateRange?.end) {
        query = query
            .gte('last_message_time', dateRange.start.toISOString())
            .lte('last_message_time', dateRange.end.toISOString());
    }

    const { data, error } = await query;
    if (error) {
        console.error('[aiChatContext] fetchConversationSummaries:', error.message);
        return [];
    }

    const base: AiConversationSummary[] = (data ?? []).map((row) => {
        const contact = row.contact as ContactEmbed;
        return {
            id: row.id,
            assignedTo: row.assigned_to ?? undefined,
            status: row.status ?? 'active',
            lastMessageTime: row.last_message_time,
            originLabel: conversationOriginLabel(contact, row.meta_attribution),
        };
    });

    const responseMap = await fetchFirstResponseMinutesByConversationIds(
        base.map((c) => c.id),
        organizationId
    );

    return base.map((c) => {
        if (!responseMap.has(c.id)) return c;
        return { ...c, firstResponseMinutes: responseMap.get(c.id) };
    });
}
